import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DivisionService } from '../services/division-service';
import httpClient from '../services/http-client';
import { useAuth } from './use-auth';
import { 
  Division, 
  DivisionsListResponse, 
  CreateDivisionRequest, 
  UpdateDivisionRequest,
  DivisionConfigImportRequest,
  DivisionConfigImportResult
} from '../types';

// Create a division service instance using the default HTTP client
const divisionService = new DivisionService(httpClient);

// Query keys
export const divisionQueryKeys = {
  all: ['divisions'] as const,
  lists: () => [...divisionQueryKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...divisionQueryKeys.lists(), filters] as const,
  details: () => [...divisionQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...divisionQueryKeys.details(), id] as const,
};

/**
 * Hook to get all divisions with optional filtering
 * Only makes API calls if user has Admin role in JWT token
 */
export function useDivisions(params?: {
  active?: boolean;
  search?: string;
}) {
  const { isAdmin: hasAdminRole } = useAuth();

  return useQuery({
    queryKey: divisionQueryKeys.list(params || {}),
    queryFn: () => divisionService.getDivisions(params),
    enabled: hasAdminRole, // Only enable the query if user has admin role
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to get a specific division by ID
 */
export function useDivision(divisionId: string, enabled = true) {
  return useQuery({
    queryKey: divisionQueryKeys.detail(divisionId),
    queryFn: () => divisionService.getDivision(divisionId),
    enabled: enabled && !!divisionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to create a new division
 */
export function useCreateDivision() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateDivisionRequest) => 
      divisionService.createDivision(request),
    onSuccess: (newDivision) => {
      // Invalidate and refetch divisions list
      queryClient.invalidateQueries({ queryKey: divisionQueryKeys.lists() });
      
      // Add the new division to the cache
      queryClient.setQueryData(
        divisionQueryKeys.detail(newDivision.id),
        newDivision
      );
    },
  });
}

/**
 * Hook to update an existing division
 */
export function useUpdateDivision() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      divisionId, 
      request 
    }: { 
      divisionId: string; 
      request: UpdateDivisionRequest; 
    }) => divisionService.updateDivision(divisionId, request),
    onSuccess: (updatedDivision) => {
      // Invalidate and refetch divisions list
      queryClient.invalidateQueries({ queryKey: divisionQueryKeys.lists() });
      
      // Update the specific division in cache
      queryClient.setQueryData(
        divisionQueryKeys.detail(updatedDivision.id),
        updatedDivision
      );
    },
  });
}

/**
 * Hook to delete a division
 */
export function useDeleteDivision() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (divisionId: string) => 
      divisionService.deleteDivision(divisionId),
    onSuccess: (_, divisionId) => {
      // Invalidate and refetch divisions list
      queryClient.invalidateQueries({ queryKey: divisionQueryKeys.lists() });
      
      // Remove the division from cache
      queryClient.removeQueries({ queryKey: divisionQueryKeys.detail(divisionId) });
    },
  });
}

/**
 * Hook to export a division's configuration as a downloadable JSON file.
 * Triggers a browser file-save dialog on success.
 */
export function useExportDivisionConfiguration() {
  return useMutation({
    mutationFn: async (divisionId: string) => {
      const blob = await divisionService.exportDivisionConfiguration(divisionId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // The API sets the Content-Disposition filename; fall back to a generic name.
      a.download = `division-config-${divisionId}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    },
  });
}

/**
 * Hook to import a previously-exported division configuration.
 */
export function useImportDivisionConfiguration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ divisionId, request }: { divisionId: string; request: DivisionConfigImportRequest }) =>
      divisionService.importDivisionConfiguration(divisionId, request),
    onSuccess: (_, { divisionId }) => {
      queryClient.invalidateQueries({ queryKey: divisionQueryKeys.detail(divisionId) });
      queryClient.invalidateQueries({ queryKey: divisionQueryKeys.lists() });
    },
  });
}

/**
 * Hook to generate (or regenerate) a division's public self-registration link slug.
 * Pass `slug` for a friendly custom link; omit it for a random one.
 */
export function useGenerateRegistrationLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ divisionId, slug }: { divisionId: string; slug?: string }) =>
      divisionService.generateRegistrationLink(divisionId, slug),
    onSuccess: (updatedDivision) => {
      queryClient.setQueryData(divisionQueryKeys.detail(updatedDivision.id), updatedDivision);
      queryClient.invalidateQueries({ queryKey: divisionQueryKeys.lists() });
    },
  });
}

/**
 * Hook to disable a division's public self-registration link.
 */
export function useClearRegistrationLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (divisionId: string) => divisionService.clearRegistrationLink(divisionId),
    onSuccess: (updatedDivision) => {
      queryClient.setQueryData(divisionQueryKeys.detail(updatedDivision.id), updatedDivision);
      queryClient.invalidateQueries({ queryKey: divisionQueryKeys.lists() });
    },
  });
}
